// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ViewerItem } from 'presentations/api/schema/user';
import { MeetingWidgetContext } from 'presentations/reducers/MeetingWidget';
import { destroy, get, patch, post } from 'infras/rest_client';
import {
  apiPath,
  dashboardCalendarsPath,
  dashboardEventEditPath,
  dashboardPath,
  eventEditPath,
} from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';
import { EventDetailItem, EventListItem } from 'presentations/api/schema/event';
import { SourceType } from 'domains/models/event_source/sourceType';
import {
  moveBackOrDefaultPath,
  moveFromOrDefaultPath,
} from 'presentations/views/helpers/router';
import { UserAppItem } from 'presentations/api/schema/account';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { isInElectron } from 'presentations/views/helpers/electron';
import { ActivityListItem } from 'presentations/api/schema/activity';

export interface Props {
  viewer: ViewerItem;
  event?: EventDetailItem;
  eventUid?: string | number;
  sourceType: SourceType | null;
}

const perPage = 20;

export const useEventContent = (props: Props) => {
  const t = translateInComponent(translations);
  const router = useRouter();

  const [isOpenDescription, setIsOpenDescription] = useState<boolean>(true);
  const [isOpenContact, setIsOpenContact] = useState<boolean>(true);
  const [isOpenWorkflow, setIsOpenWorkflow] = useState<boolean>(true);
  const [isOpenGuestList, setIsOpenGuestList] = useState<boolean>(true);
  const [isOpenPayment, setIsOpenPayment] = useState<boolean>(true);
  const [isOpenNote, setIsOpenNote] = useState<boolean>(true);

  const [
    settingMenuAnchor,
    setSettingMenuAnchor,
  ] = useState<null | HTMLElement>(null);
  const [cancelEventUid, setCancelEventUid] = useState<string | number | null>(
    null,
  );
  const [editEventUid, setEditEventUid] = useState<string | number | null>(
    null,
  );
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [isElectron, setIsElectron] = useState<boolean>(false);

  const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [isRequestAcceptLoading, setIsRequestAcceptLoading] = useState(false);
  const [isRequestAcceptDisabled, setIsRequestAcceptDisabled] = useState(false);
  const [isRequestDeclineLoading, setIsRequestDeclineLoading] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState<
    string | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  useEffect(() => {
    setActiveTab(0);
    setIsElectron(isInElectron());
  }, []);

  const meeting = useContext(MeetingWidgetContext);

  const liveUser = meeting.state.liveViewer;

  const childActiveEventsRequest = useRequest(
    props.event && props.event.isSeminar && props.event.isParent
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/${
            props.event.uid
          }/events`,
          {
            page: currentPage,
            perPage,
            status:
              props.event.reservationMethod === ReservationMethod.Poll
                ? null
                : 'open',
          },
        )
      : null,
    { isUseCacheWhenInitLoading: false },
  );
  const childActiveEvents: EventListItem[] = childActiveEventsRequest.data;
  const childActiveEventsCount = childActiveEventsRequest.totalCount;

  const childCancelEventsRequest = useRequest(
    props.event && props.event.isSeminar && props.event.isParent
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/${
            props.event.uid
          }/events`,
          {
            page: currentPage,
            perPage,
            status: 'cancel',
          },
        )
      : null,
    { isUseCacheWhenInitLoading: false },
  );
  const childCancelEvents: EventListItem[] = childCancelEventsRequest.data;
  const childCancelEventsCount = childCancelEventsRequest.totalCount;

  const userAppRequest = useRequest(apiPath(`/v1/accounts/apps`));
  const userApp: UserAppItem | null = userAppRequest.data;

  const activitiesRequest = useRequest(
    props.sourceType !== SourceType.IntegrationCalendar && props.event
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/${
            props.event.uid
          }/activities`,
        )
      : null,
  );
  const activities: ActivityListItem[] = activitiesRequest.data || [];

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const onClickBack = () => {
    moveFromOrDefaultPath(router, dashboardPath());
  };

  const onClickSettingMenu = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setSettingMenuAnchor(e.currentTarget);
  };

  const onSelectSettingMenu = async (selectedIndex: number) => {
    setSettingMenuAnchor(null);
    if (props.event?.isParent) {
      setCancelEventUid(props.eventUid || null);
    } else {
      if (props.event?.isSeminar) {
        switch (selectedIndex) {
          case 0:
            typeof props.eventUid === 'string' &&
              window.open(eventEditPath(props.eventUid));
            break;
          case 1:
            setCancelEventUid(props.eventUid || null);
            break;
          default:
            break;
        }
      } else {
        switch (selectedIndex) {
          case 0:
            if (typeof props.eventUid === 'string' && props.eventUid) {
              router.push(
                dashboardEventEditPath(props.eventUid, {
                  from: encodeURIComponent(router.asPath),
                }),
              );
            }
            break;
          case 1:
            if (props.sourceType === SourceType.EventType) {
              typeof props.eventUid === 'string' &&
                window.open(eventEditPath(props.eventUid));
            } else if (props.sourceType === SourceType.InternalCalendar) {
              let event: EventDetailItem;
              if (props.event) {
                setConfirmDelete(true);
                return;
              } else {
                const result = await get(
                  apiPath(
                    `/v1/teams/${props.viewer.team!.uid}/events/${
                      props.eventUid
                    }`,
                  ),
                );
                if (!result.isSuccess) {
                  return;
                }
                event = result.data;
                router.push(
                  dashboardEventEditPath(event.uid, {
                    from: encodeURIComponent(router.asPath),
                  }),
                );
              }
            } else if (props.sourceType === SourceType.IntegrationCalendar) {
              if (props.event && props.event.htmlLink) {
                window.open(props.event.htmlLink);
              }
            }
            break;
          case 2:
            if (props.sourceType === SourceType.EventType) {
              setCancelEventUid(props.eventUid || null);
            } else if (props.sourceType === SourceType.InternalCalendar) {
              setCancelEventUid(props.eventUid || null);
            }
            break;
          default:
            break;
        }
      }
    }
  };

  const onCloseSettingMenu = () => {
    setSettingMenuAnchor(null);
  };

  const onCancelEvent = async (event: EventListItem) => {
    if (props.sourceType === SourceType.EventType) {
      setIsCancelLoading(true);
      const result = await patch(
        apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/${props.eventUid}/cancel`,
        ),
      );
      if (!result.isSuccess) {
        setIsCancelLoading(false);
        return;
      }
      setCancelEventUid(null);
      setIsCancelLoading(false);
    } else {
      console.log(`invalid sourceType: ${props.sourceType}`);
    }

    moveBackOrDefaultPath(router, dashboardCalendarsPath());
  };

  const onCloseDialog = () => {
    setCancelEventUid(null);
    setEditEventUid(null);
    setConfirmDelete(false);
  };

  const onToggleDescription = () => {
    setIsOpenDescription(!isOpenDescription);
  };

  const onToggleNote = () => {
    setIsOpenNote(!isOpenNote);
  };

  const onToggleContact = () => {
    setIsOpenContact(!isOpenContact);
  };

  const onToggleWorkflow = () => {
    setIsOpenWorkflow(!isOpenWorkflow);
  };

  const onToggleGuestList = () => {
    setIsOpenGuestList(!isOpenGuestList);
  };

  const onTogglePayment = () => {
    setIsOpenPayment(!isOpenPayment);
  };

  const onChangePage = (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onChangeTab = async (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    const tab: string | undefined = undefined;
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  const onClickAcceptRequest = useCallback(async () => {
    setIsRequestAcceptLoading(true);
    try {
      const teamId = props.event?.teamId;
      const eventUid = props.event?.uid;
      const result = await patch(
        apiPath(`/v1/teams/${teamId}/events/${eventUid}/accept`),
      );
      if (result.isSuccess) {
        setSnackbarMessage(t('success accept request'));
        router.reload();
      } else if (result.status === 409) {
        setSnackbarErrorMessage(t('double booked text'));
        setIsRequestAcceptDisabled(true);
      } else {
        throw new Error(result.errorMessage ?? '');
      }
    } catch (e) {
      console.error(e);
      setSnackbarErrorMessage(t('failure accept request'));
    } finally {
      setIsRequestAcceptLoading(false);
    }
  }, [props.event]);

  const onClickDeclineRequest = useCallback(async () => {
    setIsRequestDeclineLoading(true);
    try {
      const teamId = props.event?.teamId;
      const eventUid = props.event?.uid;
      const result = await patch(
        apiPath(`/v1/teams/${teamId}/events/${eventUid}/decline`),
      );
      if (result.isSuccess) {
        setSnackbarMessage(t('success decline request'));
        router.reload();
      } else {
        throw new Error(result.errorMessage ?? '');
      }
    } catch (e) {
      console.error(e);
      setSnackbarErrorMessage(t('failure decline request'));
    } finally {
      setIsRequestDeclineLoading(false);
    }
  }, [props.event]);

  const onCloseSnackbar = useCallback(() => {
    setSnackbarMessage(null);
  }, []);

  const onClickEditContact = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (props.eventUid) {
      setEditEventUid(props.eventUid);
    }
  };
  const onClickDeleteEvent = async () => {
    if (!props.event) return;
    setIsCancelLoading(true);
    const result = await destroy(apiPath(`/v1/schedules/${props.event.uid}`));
    if (!result.isSuccess) {
      setSnackbarErrorMessage(t('failure delete event'));
      setIsCancelLoading(false);
      return;
    }
    setIsCancelLoading(false);
    setSnackbarErrorMessage(t('success delete event'));
    moveBackOrDefaultPath(router, dashboardCalendarsPath());
  };
  return {
    state: {
      liveUser,
      isOpenDescription,
      isOpenNote,
      isOpenContact,
      isOpenWorkflow,
      isOpenGuestList,
      isOpenPayment,
      settingMenuAnchor,
      cancelEventUid,
      editEventUid,
      currentUrl,
      isCancelLoading,
      childEvents: activeTab === 0 ? childActiveEvents : childCancelEvents,
      childEventsIsLoading:
        childActiveEventsRequest.isLoading ||
        childCancelEventsRequest.isLoading,
      childActiveEventsCount,
      childCancelEventsCount,
      currentPage,
      numberOfPages:
        activeTab === 0
          ? childActiveEventsRequest.numberOfPages
          : childCancelEventsRequest.numberOfPages,
      activeTab,
      userApp,
      isElectron,
      activities,
      isRequestAcceptLoading,
      isRequestAcceptDisabled,
      isRequestDeclineLoading,
      snackbarMessage,
      snackbarErrorMessage,
      confirmDelete,
    },
    actions: {
      onClickBack,
      onClickSettingMenu,
      onSelectSettingMenu,
      onCloseSettingMenu,
      onToggleDescription,
      onToggleNote,
      onToggleContact,
      onToggleWorkflow,
      onToggleGuestList,
      onTogglePayment,
      onCancelEvent,
      onCloseDialog,
      onChangePage,
      onChangeTab,
      onClickAcceptRequest,
      onClickDeclineRequest,
      onCloseSnackbar,
      onClickEditContact,
      onClickDeleteEvent,
    },
  };
};
